import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useWaitlist } from "../context/WaitlistContext";

const CREAM = "#FAF7F2";
const INK = "#16362A";
const LEAF = "#2F6B3F";
const BODY = "#4A6054";

const PUB = process.env.PUBLIC_URL || "";
const HERO_SLIDES = [
  {
    src: `${PUB}/hero-hd.jpg`,
    alt: "Flavrd clean-label packaging: Everyday Chilla, Supergrain Noodles and Pancake Mix on blue podiums",
  },
  {
    src: `${PUB}/hero-hd-2.jpg`,
    alt: "Flavrd clean-label packaging: Pasta, Breakfast Cereals and Muesli on blue podiums",
  },
];

/* Masked line reveal */
function RevealLine({ children, delay = 0, className = "" }) {
  return (
    <span className="line-mask">
      <motion.span
        className={`inline-block ${className}`}
        initial={{ y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.05, ease: [0.22, 1, 0.36, 1], delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 800], [0, -70]);
  const [slide, setSlide] = useState(0);
  const { openModal } = useWaitlist();

  const goTo = (next) => setSlide(next);
  const nextSlide = () => setSlide((s) => (s + 1) % HERO_SLIDES.length);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      style={{ backgroundColor: CREAM }}
      className="relative overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-16 items-center min-h-[86vh] py-14 lg:py-0">
        {/* Left */}
        <div className="flex flex-col justify-center order-1 lg:py-24">
          <h1
            data-testid="hero-headline"
            className="text-[3rem] sm:text-6xl lg:text-[4.6rem] tracking-tight leading-[1.04] mb-7"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: INK }}
          >
            <RevealLine delay={0.1}>We're re-building</RevealLine>{" "}
            <RevealLine delay={0.2}>
              <span style={{ color: "#306B3F" }}>India's trust</span>
            </RevealLine>{" "}
            <RevealLine delay={0.32}>in food.</RevealLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.66 }}
            className="text-base sm:text-lg leading-relaxed mb-10 max-w-[440px]"
            style={{ color: LEAF }}
          >
            Made with natural protein and fiber. Built for clean everyday nutrition.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.82 }}
          >
            <button
              data-testid="hero-cta-button"
              onClick={openModal}
              className="group inline-flex items-center gap-2.5 px-8 py-4 rounded-full font-semibold text-sm tracking-wide transition-all duration-300 hover:opacity-90 active:scale-95"
              style={{ backgroundColor: "#8FC69B", color: "#163A2A" }}
            >
              Shop your Protein
              <ArrowRight size={16} strokeWidth={2.5} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </motion.div>
        </div>

        {/* Right */}
        <div className="relative order-2 lg:h-[86vh] flex items-center">
          <motion.div
            className="hero-image-wrap"
            style={{
              y: imgY,
              aspectRatio: "1264 / 848",
              borderRadius: "1.75rem",
              overflow: "hidden",
              width: "100%",
              position: "relative",
              boxShadow: "0 30px 90px rgba(22,54,42,0.18)",
            }}
            data-testid="hero-carousel"
          >
            <motion.div
              className="flex h-full w-full"
              animate={{ x: `-${slide * 100}%` }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              {HERO_SLIDES.map((s, i) => (
                <div key={i} className="flex-shrink-0 w-full h-full">
                  <img
                    src={s.src}
                    alt={s.alt}
                    className="w-full h-full object-cover"
                    loading={i === 0 ? "eager" : "lazy"}
                    data-testid={`hero-slide-${i}`}
                  />
                </div>
              ))}
            </motion.div>

            {/* Dots */}
            <div className="absolute z-10 flex gap-2" style={{ left: "1.2rem", bottom: "1.5rem" }}>
              {HERO_SLIDES.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  data-testid={`hero-dot-${i}`}
                  className="rounded-full transition-all duration-300"
                  style={{
                    width: i === slide ? "22px" : "8px",
                    height: "8px",
                    backgroundColor: i === slide ? "#8FC69B" : "rgba(250,247,242,0.7)",
                  }}
                />
              ))}
            </div>
          </motion.div>

          {/* Next arrow — outside carousel so it can extend past the right edge */}
          <button
            onClick={nextSlide}
            aria-label="Next products"
            data-testid="hero-next-arrow"
            className="absolute z-10 grid place-items-center rounded-full transition-all duration-300 hover:scale-105 active:scale-95"
            style={{
              right: "-1rem",
              top: "50%",
              transform: "translateY(-50%)",
              width: "3.25rem",
              height: "3.25rem",
              backgroundColor: "rgba(250,247,242,0.9)",
              border: "2px solid #8FC69B",
              backdropFilter: "blur(6px)",
              boxShadow: "0 8px 24px rgba(22,54,42,0.18)",
            }}
          >
            <ArrowRight size={22} strokeWidth={2.5} color="#8FC69B" />
          </button>
        </div>
      </div>
    </section>
  );
}

